import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { BASE_API_URL } from "./config";

// ── Constants ─────────────────────────────────────────────────────────────────
const REALM_ICONS = {
  Animals:"🐾", Birds:"🐦", Plants:"🌿", People:"👥",
  Places:"📍", Science:"🔬", History:"📜", Sports:"⚽", Food:"🍎", Programming:"💻", Other:"📌",
};
const HABITAT_ICONS = {
  Jungle:"🌴", Ocean:"🌊", Land:"🏕️", Sky:"☁️", Desert:"🏜️",
  River:"🏞️", Country:"🌍", Forest:"🌲", Farm:"🌾", Mountain:"⛰️",
  Wetland:"💧", Space:"🚀", Lab:"🧪", Nature:"🌎", Technology:"💻",
  City:"🏙️", Village:"🏡", Ancient:"🏛️", Medieval:"⚔️", Modern:"🏢",
  Field:"⚽", Court:"🏀", Pool:"🏊", Track:"🏃", Indoor:"🎾",
  Fruit:"🍎", Vegetable:"🥦", Grain:"🌾", Seafood:"🐟", Dairy:"🥛", Spice:"🌶️",
  Garden:"🌷", General:"📌",
};

// ── Device helpers ────────────────────────────────────────────────────────────
const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// ── Voice helpers (copied from TamilLearningSlide pattern) ────────────────────
function getBestVoice(lang) {
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  let v = voices.find(v => v.lang === lang);
  if (v) return v;
  const prefix = lang.split("-")[0];
  v = voices.find(v => v.lang.startsWith(prefix));
  if (v) return v;
  // Tamil fallback: en-IN sounds closest to Tamil cadence on devices without Tamil voice
  if (lang.startsWith("ta")) {
    v = voices.find(v => v.lang === "en-IN") || voices.find(v => v.lang.startsWith("en"));
    if (v) return v;
  }
  return null;
}

function waitForVoices() {
  return new Promise(resolve => {
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) { resolve(voices); return; }
    const handler = () => {
      window.speechSynthesis.removeEventListener("voiceschanged", handler);
      resolve(window.speechSynthesis.getVoices());
    };
    window.speechSynthesis.addEventListener("voiceschanged", handler);
    setTimeout(() => resolve(window.speechSynthesis.getVoices()), 2000);
  });
}

// iOS kills TTS after silence — keep it alive with pause/resume
let _keepAliveTimer = null;
function startKeepAlive() {
  if (!isIOS()) return;
  stopKeepAlive();
  _keepAliveTimer = setInterval(() => {
    if (!window.speechSynthesis.speaking) return;
    window.speechSynthesis.pause();
    window.speechSynthesis.resume();
  }, 10000);
}
function stopKeepAlive() {
  if (_keepAliveTimer) { clearInterval(_keepAliveTimer); _keepAliveTimer = null; }
}

// ── Text helpers ──────────────────────────────────────────────────────────────
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "";

const fmtDateForSpeech = (d, lang) => {
  if (!d) return "";
  const date = new Date(d);
  if (lang === "ta") {
    // Tamil date announcement
    const months = ["ஜனவரி","பிப்ரவரி","மார்ச்","ஏப்ரல்","மே","ஜூன்","ஜூலை","ஆகஸ்ட்","செப்டம்பர்","அக்டோபர்","நவம்பர்","டிசம்பர்"];
    return `தேதி: ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  }
  return `Date: ${fmtDate(d)}`;
};

// Split content into readable chunks (numbered points or sentences)
function splitSentences(text) {
  if (!text) return [];
  // If numbered list (1. ... 2. ...) — split by line
  if (/^\d+\.\s/.test(text.trim())) {
    return text.split(/\n/).map(s => s.trim()).filter(Boolean);
  }
  // Otherwise split by sentence
  return text.match(/[^.!?।\n]+[.!?।\n]*/g)?.map(s => s.trim()).filter(Boolean) || [text];
}

// Image resolver
const getImage = (note) => {
  if (!note) return null;
  const url = (note.image_url || "").trim();
  if (url.startsWith("http")) return url;
  if (url) return `/resources/${url}`;
  const name = (note.topic || "").trim();
  return name ? `/resources/${name}.png` : null;
};

// ── TTS hook — iOS-safe, sentence-level, date-announcing ─────────────────────
function useTTS() {
  const [speaking,      setSpeaking]      = useState(false);
  const [activeSentIdx, setActiveSentIdx] = useState(-1); // highlighted sentence
  const [voiceWarning,  setVoiceWarning]  = useState(""); // Tamil not available msg
  const stopRef   = useRef(false);
  const voicesRef = useRef([]);

  // Pre-load voices on mount (critical for iOS)
  useEffect(() => {
    waitForVoices().then(v => { voicesRef.current = v; });
    const handler = () => { voicesRef.current = window.speechSynthesis.getVoices(); };
    window.speechSynthesis?.addEventListener("voiceschanged", handler);
    return () => window.speechSynthesis?.removeEventListener("voiceschanged", handler);
  }, []);

  const stop = useCallback(() => {
    stopRef.current = true;
    window.speechSynthesis?.cancel();
    stopKeepAlive();
    setSpeaking(false);
    setActiveSentIdx(-1);
  }, []);

  // Speak one text chunk; returns promise that resolves when done
  const speakOne = useCallback((text, lang) =>
    new Promise(resolve => {
      if (!window.speechSynthesis || stopRef.current || !text.trim()) { resolve(); return; }
      const u  = new SpeechSynthesisUtterance(text);
      u.lang   = lang === "ta" ? "ta-IN" : "en-IN";
      u.rate   = 0.85;
      const voice = getBestVoice(u.lang);
      if (voice) u.voice = voice;
      u.onend   = resolve;
      u.onerror = resolve;
      window.speechSynthesis.speak(u);
    }), []);

  // Speak a single note with sentence-level highlighting + date announcement
  const speakNote = useCallback(async (note, lang, onSentChange) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    await waitForVoices();
    stopRef.current = false;
    setSpeaking(true);
    setVoiceWarning("");

    // Check Tamil voice availability
    if (lang === "ta") {
      const v = getBestVoice("ta-IN");
      if (v && !v.lang.startsWith("ta")) {
        setVoiceWarning("Tamil voice not installed on this device — reading in English.");
      }
    }

    startKeepAlive();

    // 1. Announce date
    const dateAnnounce = fmtDateForSpeech(note.note_date, lang);
    if (dateAnnounce && !stopRef.current) await speakOne(dateAnnounce, lang);

    // 2. Announce topic
    const topicText = lang === "ta" ? (note.topic_tamil || note.topic) : note.topic;
    if (topicText && !stopRef.current) await speakOne(topicText, lang);

    // 3. Read sentences one by one with highlighting
    const content   = lang === "ta" ? (note.content_tamil || note.content_eng) : (note.content_eng || note.content_tamil);
    const sentences = splitSentences(content);
    for (let i = 0; i < sentences.length; i++) {
      if (stopRef.current) break;
      setActiveSentIdx(i);
      if (onSentChange) onSentChange(i);
      await speakOne(sentences[i], lang);
    }

    stopKeepAlive();
    if (!stopRef.current) {
      setSpeaking(false);
      setActiveSentIdx(-1);
      if (onSentChange) onSentChange(-1);
    }
  }, [speakOne]);

  // Speak a list of notes in order (date announced before each)
  const speakList = useCallback(async (notes, lang, onNoteChange, onSentChange) => {
    if (!window.speechSynthesis || !notes.length) return;
    window.speechSynthesis.cancel();
    await waitForVoices();
    stopRef.current = false;
    setSpeaking(true);
    startKeepAlive();

    for (let ni = 0; ni < notes.length; ni++) {
      if (stopRef.current) break;
      const note = notes[ni];
      if (onNoteChange) onNoteChange(ni);

      const dateAnnounce = fmtDateForSpeech(note.note_date, lang);
      if (dateAnnounce && !stopRef.current) await speakOne(dateAnnounce, lang);

      const topicText = lang === "ta" ? (note.topic_tamil || note.topic) : note.topic;
      if (topicText && !stopRef.current) await speakOne(topicText, lang);

      const content   = lang === "ta" ? (note.content_tamil || note.content_eng) : (note.content_eng || note.content_tamil);
      const sentences = splitSentences(content);
      for (let i = 0; i < sentences.length; i++) {
        if (stopRef.current) break;
        setActiveSentIdx(i);
        if (onSentChange) onSentChange(i);
        await speakOne(sentences[i], lang);
      }
      setActiveSentIdx(-1);
      if (onSentChange) onSentChange(-1);
      if (!stopRef.current && ni < notes.length - 1) await new Promise(r => setTimeout(r, 800));
    }

    stopKeepAlive();
    if (!stopRef.current) { setSpeaking(false); setActiveSentIdx(-1); }
  }, [speakOne]);

  return { speaking, activeSentIdx, voiceWarning, speakNote, speakList, stop };
}

// ── Content renderer with sentence highlighting ───────────────────────────────
function HighlightedContent({ content, lang, activeSentIdx }) {
  const sentences = useMemo(() => splitSentences(content), [content]);

  return (
    <div style={{ fontSize:lang==="ta"?18:16, lineHeight:lang==="ta"?2.1:1.9, fontFamily:lang==="ta"?"'Noto Sans Tamil',sans-serif":"inherit", wordBreak:"break-word" }}>
      {sentences.map((s, i) => (
        <span key={i} style={{
          display:"inline",
          background: i === activeSentIdx ? "rgba(253,224,71,0.25)" : "transparent",
          color:      i === activeSentIdx ? "#fff"
                    : lang === "ta"       ? "#fcd34d"
                    :                       "#cbd5e1",
          borderRadius:4,
          boxShadow:  i === activeSentIdx ? "0 0 0 2px rgba(253,224,71,0.4)" : "none",
          padding:    i === activeSentIdx ? "1px 3px" : "0",
          transition: "background 0.25s, color 0.25s",
        }}>
          {s}{" "}
        </span>
      ))}
    </div>
  );
}

// ── Quiz card — button that opens a focused modal with the comprehension MCQ ─
function QuizCard({ note, lang, onAnswer }) {
  const [open,     setOpen]     = useState(false);
  const [picked,   setPicked]   = useState("");
  const [revealed, setRevealed] = useState(false);

  // Reset everything when the active note changes (user swipes to next date)
  useEffect(() => {
    setPicked("");
    setRevealed(false);
    setOpen(false);
  }, [note.id]);

  // ESC closes the quiz modal
  useEffect(() => {
    if (!open) return;
    const fn = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [open]);

  // Lock background scroll while the quiz modal is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const hasQuiz = !!(note.question_text || "").trim();
  if (!hasQuiz) return null;

  const qText = (lang === "ta" && note.question_text_tamil)
    ? note.question_text_tamil
    : (note.question_text || note.question_text_tamil || "");

  const options = [
    { letter: "A", text: note.option_a },
    { letter: "B", text: note.option_b },
    { letter: "C", text: note.option_c },
    { letter: "D", text: note.option_d },
  ].filter(o => (o.text || "").trim() !== "");

  const correct = (note.correct_option || "").trim().toUpperCase();

  const handlePick = (letter) => {
    if (revealed) return;
    setPicked(letter);
    setRevealed(true);
    if (onAnswer) onAnswer(note.id, letter, letter === correct);
  };
  const handleRetry = () => { setPicked(""); setRevealed(false); };

  const isCorrect  = picked === correct;
  const tamilFont  = lang === "ta" ? "'Noto Sans Tamil',sans-serif" : "inherit";

  // Trigger button reflects current attempt state
  const buttonLabel = !revealed
    ? (lang === "ta" ? "📝 வினாவுக்கு பதிலளி" : "📝 Take the Quiz")
    : isCorrect
      ? (lang === "ta" ? "✅ சரி பதிலளித்தீர்கள் — மீண்டும் பார்" : "✅ Answered Correctly — View Again")
      : (lang === "ta" ? "🔄 மீண்டும் முயற்சிக்க" : "🔄 Try Again");

  const buttonBg = !revealed
    ? "linear-gradient(135deg,#7c3aed 0%,#5b21b6 100%)"
    : isCorrect
      ? "linear-gradient(135deg,#16a34a 0%,#15803d 100%)"
      : "linear-gradient(135deg,#dc2626 0%,#991b1b 100%)";

  return (
    <>
      {/* ── Trigger button (shown inline under the passage) ──────────────── */}
      <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <button
          onClick={() => setOpen(true)}
          style={{
            width: "100%",
            background: buttonBg,
            color: "#fff",
            border: "none",
            borderRadius: 14,
            padding: "16px 22px",
            fontSize: 16,
            fontWeight: 800,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            fontFamily: tamilFont,
            boxShadow: "0 6px 18px rgba(124,58,237,0.35)",
            transition: "transform 0.15s, box-shadow 0.15s",
          }}
          onMouseDown={e => e.currentTarget.style.transform = "scale(0.98)"}
          onMouseUp={e   => e.currentTarget.style.transform = "scale(1)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
        >
          <span>{buttonLabel}</span>
          <span style={{ fontSize: 20, opacity: 0.85 }}>→</span>
        </button>
        <div style={{
          fontSize: 11, color: "#64748b", textAlign: "center",
          marginTop: 8, fontFamily: tamilFont,
        }}>
          {lang === "ta"
            ? "முதலில் வாசிக்கவும் — பின்னர் பதிலளிக்கவும்"
            : "Read first, then test your understanding"}
        </div>
      </div>

      {/* ── Quiz Modal Overlay ───────────────────────────────────────────── */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 3000,
            background: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            animation: "vk-fade-in 0.18s ease-out",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "linear-gradient(145deg,#1e1b4b 0%,#0f172a 60%,#1a103e 100%)",
              border: "1px solid rgba(124,58,237,0.45)",
              borderRadius: 20,
              padding: "22px 22px 24px",
              maxWidth: 560,
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 24px 70px rgba(0,0,0,0.7)",
              position: "relative",
            }}
          >
            {/* Close button */}
            <button
              onClick={() => setOpen(false)}
              aria-label="Close quiz"
              style={{
                position: "absolute",
                top: 12, right: 12,
                background: "rgba(255,255,255,0.12)",
                border: "none",
                color: "#fff",
                fontSize: 18,
                width: 36, height: 36,
                borderRadius: "50%",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ✕
            </button>

            {/* Header */}
            <div style={{
              fontSize: 11, color: "#c4b5fd", fontWeight: 800, letterSpacing: 1, marginBottom: 6,
            }}>
              📝 {lang === "ta" ? "வினா நேரம் / QUIZ TIME" : "QUIZ TIME / வினா நேரம்"}
            </div>
            <div style={{
              fontSize: 12, color: "#94a3b8", marginBottom: 18, fontFamily: tamilFont,
            }}>
              {lang === "ta"
                ? `${note.topic_tamil || note.topic} பற்றிய வினா`
                : `Question about ${note.topic}`}
            </div>

            {/* Question */}
            <div style={{
              fontSize: lang === "ta" ? 20 : 18,
              fontWeight: 700,
              color: "#fff",
              lineHeight: 1.5,
              marginBottom: 18,
              fontFamily: tamilFont,
            }}>
              {qText}
            </div>

            {/* Options */}
            <div style={{ display: "grid", gap: 10 }}>
              {options.map(opt => {
                const isPicked    = picked === opt.letter;
                const isAns       = revealed && opt.letter === correct;
                const isWrongPick = revealed && isPicked && !isAns;

                let bg = "rgba(255,255,255,0.06)";
                let border = "rgba(255,255,255,0.18)";
                let color = "#e2e8f0";
                let circleBg = "rgba(255,255,255,0.12)";

                if (isAns)            { bg = "rgba(22,163,74,0.25)"; border = "#16a34a"; color = "#fff"; circleBg = "#16a34a"; }
                else if (isWrongPick) { bg = "rgba(220,38,38,0.25)"; border = "#dc2626"; color = "#fff"; circleBg = "#dc2626"; }

                return (
                  <button
                    key={opt.letter}
                    onClick={() => handlePick(opt.letter)}
                    disabled={revealed}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      background: bg,
                      border: `1px solid ${border}`,
                      borderRadius: 12,
                      padding: "13px 15px",
                      color,
                      cursor: revealed ? "default" : "pointer",
                      textAlign: "left",
                      fontSize: 15,
                      fontFamily: tamilFont,
                      width: "100%",
                      transition: "all 0.2s",
                    }}
                  >
                    <span style={{
                      width: 32, height: 32, borderRadius: "50%",
                      background: circleBg,
                      color: "#fff",
                      fontSize: 14, fontWeight: 800,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      {opt.letter}
                    </span>
                    <span style={{ flex: 1, lineHeight: 1.35 }}>{opt.text}</span>
                    {isAns       && <span style={{ fontSize: 20, color: "#4ade80", fontWeight: 800 }}>✓</span>}
                    {isWrongPick && <span style={{ fontSize: 20, color: "#fca5a5", fontWeight: 800 }}>✗</span>}
                  </button>
                );
              })}
            </div>

            {/* Feedback after answer */}
            {revealed && (
              <div style={{
                marginTop: 16,
                padding: "13px 15px",
                background: isCorrect ? "rgba(22,163,74,0.15)" : "rgba(220,38,38,0.15)",
                border: `1px solid ${isCorrect ? "rgba(22,163,74,0.5)" : "rgba(220,38,38,0.5)"}`,
                borderRadius: 11,
              }}>
                <div style={{
                  fontSize: 15,
                  color: isCorrect ? "#4ade80" : "#fca5a5",
                  fontWeight: 800,
                  fontFamily: tamilFont,
                  marginBottom: 10,
                }}>
                  {isCorrect
                    ? (lang === "ta" ? "🎉 சரியான பதில்! நன்று!" : "🎉 Correct! Well done!")
                    : (lang === "ta" ? `❌ தவறு. சரியான பதில்: ${correct}` : `❌ Incorrect. The correct answer is: ${correct}`)}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={handleRetry} style={{
                    background: "rgba(255,255,255,0.14)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 9,
                    padding: "9px 16px",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: tamilFont,
                  }}>
                    🔄 {lang === "ta" ? "மீண்டும் முயல்" : "Try again"}
                  </button>
                  <button onClick={() => setOpen(false)} style={{
                    background: "rgba(96,165,250,0.18)",
                    color: "#93c5fd",
                    border: "1px solid rgba(96,165,250,0.45)",
                    borderRadius: 9,
                    padding: "9px 16px",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: tamilFont,
                  }}>
                    📖 {lang === "ta" ? "வாசிப்புக்கு திரும்பு" : "Back to reading"}
                  </button>
                </div>
              </div>
            )}
          </div>

          <style>{`@keyframes vk-fade-in { from{opacity:0;transform:scale(0.98)} to{opacity:1;transform:scale(1)} }`}</style>
        </div>
      )}
    </>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const ST = {
  tag:    c      => ({ background:c, color:"#fff", borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:700 }),
  chip:   active => ({ background:active?"#16a34a":"rgba(255,255,255,0.1)", color:active?"#fff":"#94a3b8", borderRadius:20, padding:"5px 14px", fontSize:13, fontWeight:active?700:400, cursor:"pointer", border:"none" }),
  btn:    c      => ({ background:c, color:"#fff", border:"none", borderRadius:9, padding:"7px 16px", fontWeight:700, fontSize:13, cursor:"pointer" }),
  arrow:  side   => ({ position:"absolute", top:"50%", [side]:10, transform:"translateY(-50%)", background:"rgba(0,0,0,0.55)", color:"#fff", border:"none", borderRadius:"50%", width:44, height:44, fontSize:26, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }),
  navBtn: { background:"rgba(255,255,255,0.08)", color:"#fff", border:"none", borderRadius:9, padding:"7px 16px", fontWeight:700, fontSize:13, cursor:"pointer" },
};

// ── Slide Modal ───────────────────────────────────────────────────────────────
function SlideModal({ notes, startIndex, autoPlay, lang: chosenLang, onClose }) {
  // Language is the student's ONE-TIME choice from the start screen
  // (LanguageChoiceScreen) — it must stay fixed for the whole session, not
  // silently switch per note based on which language happens to have
  // content (that was the actual bug: it used to recompute from
  // note.content_tamil on every note change). The chip buttons below still
  // let the student manually override it if they want.
  const [idx,   setIdx]   = useState(startIndex);
  const [lang,  setLang]  = useState(chosenLang || "en");
  const [imgOk, setImgOk] = useState(true);
  const [localSentIdx, setLocalSentIdx] = useState(-1);
  // Quiz scoring across notes: { [noteId]: true|false }
  const [scores, setScores] = useState({});
  const handleQuizAnswer = useCallback((noteId, _picked, correct) => {
    setScores(s => ({ ...s, [noteId]: correct }));
  }, []);
  const totalAttempted = Object.keys(scores).length;
  const totalCorrect   = Object.values(scores).filter(Boolean).length;
  const quizCount      = notes.filter(n => (n.question_text || "").trim() !== "").length;
  const dateBarRef = useRef(null);
  const { speaking, activeSentIdx, voiceWarning, speakNote, speakList, stop } = useTTS();
  const note = notes[idx];
  const multiDate = notes.length > 1;

  // Sync activeSentIdx → local
  useEffect(() => { setLocalSentIdx(activeSentIdx); }, [activeSentIdx]);
  // Reset on note change — deliberately does NOT touch `lang` (see note above).
  useEffect(() => {
    setImgOk(true);
    setLocalSentIdx(-1);
  }, [idx, notes]);
  // Stop TTS on unmount
  useEffect(() => () => stop(), [stop]);
  // Auto-play if requested (Read Latest triggers this)
  useEffect(() => {
    if (!autoPlay) return;
    const timer = setTimeout(() => speakNote(notes[0], chosenLang || "en", setLocalSentIdx), 600);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line
  // Scroll date bar to active
  useEffect(() => {
    dateBarRef.current?.querySelector("[data-active='true']")
      ?.scrollIntoView({ behavior:"smooth", block:"nearest", inline:"center" });
  }, [idx]);
  // Keyboard nav
  useEffect(() => {
    const fn = e => {
      if (e.key==="ArrowLeft")  setIdx(i => Math.max(0, i-1));
      if (e.key==="ArrowRight") setIdx(i => Math.min(notes.length-1, i+1));
      if (e.key==="Escape")     onClose();
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [notes.length, onClose]);

  if (!note) return null;
  const imgSrc  = getImage(note);
  const content = lang==="ta" ? (note.content_tamil || note.content_eng) : (note.content_eng || note.content_tamil);
  const shareURL  = "https://virtualkalvi.com/reading";
  const shareText = `📖 I read about ${note.topic}${note.topic_tamil?" ("+note.topic_tamil+")":""} on VirtualKalvi!\n`;

  const handleShare = (platform) => {
    const encoded = encodeURIComponent(shareText + shareURL);
    if (platform === "wa")   window.open(`https://wa.me/?text=${encoded}`, "_blank");
    if (platform === "fb")   window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareURL)}`, "_blank");
    if (platform === "native" && navigator.share) {
      navigator.share({ title:`VirtualKalvi — ${note.topic}`, text:shareText, url:shareURL }).catch(()=>{});
    }
  };

  const handleReadAll = () => {
    if (speaking) { stop(); return; }
    speakList(notes, lang, (ni) => setIdx(ni), setLocalSentIdx);
  };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:2000, background:"#000", display:"flex", flexDirection:"column" }}>

      {/* ── Top bar ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", background:"rgba(0,0,0,0.85)", borderBottom:"1px solid rgba(255,255,255,0.08)", flexShrink:0 }}>
        <div style={{ display:"flex", gap:6, alignItems:"center", overflow:"hidden" }}>
          <span style={ST.tag("#1e3a5f")}>{note.realm}</span>
          <span style={{ color:"#475569" }}>›</span>
          <span style={ST.tag("#14532d")}>{note.habitat}</span>
          {/* Shows whichever language was chosen first; the other language
              (if present) follows in parentheses, smaller/muted. This used
              to always show Tamil first regardless of `lang`. */}
          {lang === "ta" && note.topic_tamil
            ? <><span style={{ color:"#fbbf24", fontWeight:800, fontSize:15, fontFamily:"'Noto Sans Tamil',sans-serif" }}>{note.topic_tamil}</span>
                <span style={{ color:"#94a3b8", fontSize:13 }}>({note.topic})</span></>
            : <><span style={{ color:"#fff", fontWeight:800, fontSize:15 }}>{note.topic}</span>
                {note.topic_tamil && <span style={{ color:"#94a3b8", fontSize:13, fontFamily:"'Noto Sans Tamil',sans-serif" }}>({note.topic_tamil})</span>}</>
          }
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
          {totalAttempted > 0 && (
            <span style={{
              background: totalCorrect === totalAttempted ? "rgba(22,163,74,0.25)" : "rgba(124,58,237,0.25)",
              border: `1px solid ${totalCorrect === totalAttempted ? "rgba(22,163,74,0.5)" : "rgba(124,58,237,0.5)"}`,
              color: "#fff",
              borderRadius: 14,
              padding: "3px 10px",
              fontSize: 12,
              fontWeight: 700,
              whiteSpace: "nowrap",
            }} title={`Quiz score: ${totalCorrect} of ${totalAttempted} attempted${quizCount > 1 ? ` · ${quizCount} quizzes total` : ""}`}>
              📊 {totalCorrect}/{totalAttempted}
            </span>
          )}
          <button onClick={onClose} style={{ background:"none", border:"none", color:"#fff", fontSize:24, cursor:"pointer" }}>✕</button>
        </div>
      </div>

      {/* ── Date selector strip ── */}
      {multiDate && (
        <div ref={dateBarRef} style={{ display:"flex", gap:8, overflowX:"auto", padding:"8px 14px", background:"rgba(0,0,0,0.7)", borderBottom:"1px solid rgba(255,255,255,0.07)", flexShrink:0, scrollbarWidth:"none" }}>
          {notes.map((n, i) => (
            <button key={i} data-active={i===idx?"true":"false"}
              onClick={() => { setIdx(i); stop(); }}
              style={{ flexShrink:0, background:i===idx?"#16a34a":"rgba(255,255,255,0.1)", color:"#fff", border:"none", borderRadius:8, padding:"5px 12px", fontSize:12, fontWeight:i===idx?700:400, cursor:"pointer" }}>
              📅 {fmtDate(n.note_date)}
            </button>
          ))}
        </div>
      )}

      {/* ── IMAGE — centre stage ── */}
      <div style={{ flex:"0 0 auto", height:multiDate?"38vh":"44vh", background:"linear-gradient(180deg,#0d1b0d,#000)", display:"flex", alignItems:"center", justifyContent:"center", position:"relative", overflow:"hidden" }}>
        {imgSrc && imgOk
          ? <img src={imgSrc} alt={note.topic} style={{ maxHeight:"100%", maxWidth:"100%", objectFit:"contain" }} onError={() => setImgOk(false)} />
          : <span style={{ fontSize:80, userSelect:"none" }}>{REALM_ICONS[note.realm]||"📖"}</span>
        }
        {/* Date overlay */}
        <div style={{ position:"absolute", bottom:8, left:"50%", transform:"translateX(-50%)", background:"rgba(0,0,0,0.65)", borderRadius:20, padding:"4px 16px", backdropFilter:"blur(4px)", whiteSpace:"nowrap" }}>
          <span style={{ color:"#fff", fontWeight:700, fontSize:13 }}>📅 {fmtDate(note.note_date)}</span>
          {multiDate && <span style={{ color:"#64748b", fontSize:11, marginLeft:8 }}>{idx+1} / {notes.length}</span>}
        </div>
        {idx > 0 && <button onClick={() => { stop(); setIdx(i=>i-1); }} style={ST.arrow("left")}>‹</button>}
        {idx < notes.length-1 && <button onClick={() => { stop(); setIdx(i=>i+1); }} style={ST.arrow("right")}>›</button>}
      </div>

      {/* ── DESCRIPTION — scrollable ── */}
      <div style={{ flex:"1 1 auto", overflowY:"auto", background:"linear-gradient(180deg,#0f2027,#1a2e1a)", padding:"12px 16px 20px" }}>

        {/* Voice warning (iPhone Tamil not installed) */}
        {voiceWarning && (
          <div style={{ background:"rgba(245,158,11,0.15)", border:"1px solid rgba(245,158,11,0.4)", borderRadius:8, padding:"7px 12px", fontSize:12, color:"#fbbf24", marginBottom:10 }}>
            ⚠️ {voiceWarning}
          </div>
        )}

        {/* Lang toggle — Tamil FIRST + TTS controls */}
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:12, alignItems:"center" }}>
          {/* Tamil first */}
          {note.content_tamil && <button style={ST.chip(lang==="ta")} onClick={() => { stop(); setLang("ta"); }}>🇮🇳 தமிழ்</button>}
          <button style={ST.chip(lang==="en")} onClick={() => { stop(); setLang("en"); }}>🇬🇧 English</button>
          {!speaking
            ? <button style={ST.btn("#16a34a")} onClick={() => speakNote(note, lang, setLocalSentIdx)}>🔊 வாசி / Read</button>
            : <button style={ST.btn("#dc2626")} onClick={stop}>⏹ நிறுத்து / Stop</button>}
          {multiDate && (
            <button style={{ ...ST.btn("#1d4ed8"), fontSize:11, padding:"5px 10px" }} onClick={handleReadAll}>
              {speaking ? "⏹" : "🔊"} All {notes.length} dates
            </button>
          )}
          {note.added_by && <span style={{ fontSize:11, color:"#64748b", marginLeft:"auto" }}>✍️ {note.added_by}</span>}
        </div>

        {/* Highlighted content */}
        <HighlightedContent
          content={content}
          lang={lang}
          activeSentIdx={localSentIdx}
        />

        {/* ── Comprehension Quiz ── */}
        <QuizCard note={note} lang={lang} onAnswer={handleQuizAnswer} />

        {/* ── Share bar ── */}
        <div style={{ marginTop:20, paddingTop:14, borderTop:"1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ fontSize:11, color:"#64748b", marginBottom:8, fontWeight:600 }}>SHARE THIS NOTE</div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            <button onClick={() => handleShare("wa")}
              style={{ background:"#25d366", color:"#fff", border:"none", borderRadius:9, padding:"7px 16px", fontWeight:700, fontSize:13, cursor:"pointer" }}>
              💬 WhatsApp
            </button>
            <button onClick={() => handleShare("fb")}
              style={{ background:"#1877f2", color:"#fff", border:"none", borderRadius:9, padding:"7px 16px", fontWeight:700, fontSize:13, cursor:"pointer" }}>
              👍 Facebook
            </button>
            {navigator.share && (
              <button onClick={() => handleShare("native")}
                style={{ background:"#e1306c", color:"#fff", border:"none", borderRadius:9, padding:"7px 16px", fontWeight:700, fontSize:13, cursor:"pointer" }}>
                📤 Share / Instagram
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Bottom nav ── */}
      {multiDate && (
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 16px", background:"rgba(0,0,0,0.85)", borderTop:"1px solid rgba(255,255,255,0.08)", flexShrink:0 }}>
          <button style={ST.navBtn} disabled={idx===0} onClick={() => { stop(); setIdx(i=>i-1); }}>◀ முந்தைய</button>
          <span style={{ color:"#64748b", fontSize:12 }}>{idx+1} / {notes.length}</span>
          <button style={ST.navBtn} disabled={idx===notes.length-1} onClick={() => { stop(); setIdx(i=>i+1); }}>அடுத்த ▶</button>
        </div>
      )}
    </div>
  );
}

// ── Level 0: Home ─────────────────────────────────────────────────────────────
function HomeLevel({ realms, onSelect }) {
  return (
    <>
      <div style={{ textAlign:"center", padding:"16px 0 10px" }}>
        <div style={{ fontSize:28, fontWeight:800, color:"#fff" }}>📖 தினசரி வாசிப்பு</div>
        <div style={{ fontSize:13, color:"#94a3b8", marginTop:3 }}>Daily Reading — Choose a world</div>
      </div>
      <Link to="/tnpsc" style={{
        display:"block", textDecoration:"none", background:"linear-gradient(135deg,#312e81,#1e1b4b)",
        border:"1px solid rgba(168,85,247,0.35)", borderRadius:14, padding:"14px 16px", margin:"4px 0 16px",
      }}>
        <div style={{ fontWeight:800, fontSize:14, color:"#fff" }}>🎓 TNPSC 554 Menu</div>
        <div style={{ fontSize:12, color:"#c4b5fd", marginTop:2 }}>5 units — C/Python/OOP, Data Structures, Computer Architecture, DBMS, OS & Cloud — guides + 788 practice questions →</div>
      </Link>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))", gap:14, padding:"6px 0" }}>
        {realms.map(r => (
          <div key={r.realm} onClick={() => onSelect(r.realm)}
            style={{ background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:18, padding:"22px 12px", textAlign:"center", cursor:"pointer" }}>
            <div style={{ fontSize:50 }}>{REALM_ICONS[r.realm]||"📌"}</div>
            <div style={{ fontWeight:700, fontSize:15, color:"#fff", marginTop:10 }}>{r.realm}</div>
            <div style={{ fontSize:12, color:"#64748b", marginTop:3 }}>{r.count} note{r.count!==1?"s":""}</div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Level 1: Habitats ─────────────────────────────────────────────────────────
function HabitatLevel({ realm, habitats, onSelect }) {
  return (
    <>
      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:24, fontWeight:800, color:"#fff" }}>{REALM_ICONS[realm]||"📌"} {realm}</div>
        <div style={{ fontSize:13, color:"#94a3b8", marginTop:3 }}>Choose a habitat</div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))", gap:14 }}>
        {habitats.map(h => (
          <div key={h.habitat} onClick={() => onSelect(h.habitat)}
            style={{ background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:16, padding:"18px 10px", textAlign:"center", cursor:"pointer" }}>
            <div style={{ fontSize:44 }}>{HABITAT_ICONS[h.habitat]||"🌿"}</div>
            <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8 }}>{h.habitat}</div>
            <div style={{ fontSize:12, color:"#64748b", marginTop:2 }}>{h.count} note{h.count!==1?"s":""}</div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Level 2: Topic cards (one per unique topic, count = dates) ────────────────
function TopicCard({ group, lang, onClick }) {
  const [imgOk, setImgOk] = useState(true);
  const imgSrc = getImage(group);
  // Whichever language the student chose is shown big/bright FIRST, the
  // other language (if this topic has one) shown small/muted second —
  // this used to hardcode Tamil first regardless of the chosen language,
  // which is the "still shows Tamil after picking English" bug.
  const showTamilFirst = lang === "ta" && !!group.topic_tamil;
  const primaryText   = showTamilFirst ? group.topic_tamil : group.topic;
  const secondaryText = showTamilFirst ? group.topic : (lang !== "ta" ? group.topic_tamil : "");
  const primaryIsTamil = showTamilFirst;

  return (
    <div onClick={onClick}
      style={{ background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:16, overflow:"hidden", cursor:"pointer", position:"relative" }}>
      <div style={{ height:165, background:"linear-gradient(180deg,#0d1b0d,#0f172a)", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
        {imgSrc && imgOk
          ? <img src={imgSrc} alt={group.topic} style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={() => setImgOk(false)} />
          : <span style={{ fontSize:64, userSelect:"none" }}>{REALM_ICONS[group.realm]||"📖"}</span>
        }
      </div>
      {group.count > 1 && (
        <div style={{ position:"absolute", top:10, right:10, background:"#16a34a", color:"#fff", borderRadius:20, padding:"2px 9px", fontSize:11, fontWeight:700 }}>
          📅 {group.count} dates
        </div>
      )}
      <div style={{ padding:"11px 12px 14px" }}>
        <div style={{
          fontWeight:800, fontSize:16, color: primaryIsTamil ? "#fbbf24" : "#fff",
          fontFamily: primaryIsTamil ? "'Noto Sans Tamil',sans-serif" : "inherit",
        }}>
          {primaryText}
        </div>
        {secondaryText && (
          <div style={{
            fontWeight:500, fontSize:13, color:"#94a3b8", marginTop:2,
            fontFamily: !primaryIsTamil && secondaryText === group.topic_tamil ? "'Noto Sans Tamil',sans-serif" : "inherit",
          }}>
            {secondaryText}
          </div>
        )}
        <div style={{ fontSize:11, color:"#64748b", marginTop:5 }}>
          {group.count===1 ? fmtDate(group.latestDate) : `${fmtDate(group.latestDate)} · ${group.count} notes`}
        </div>
      </div>
    </div>
  );
}

function TopicsLevel({ realm, habitat, notes, lang, onOpenTopic }) {
  const groups = useMemo(() => {
    const map = {};
    notes.forEach(n => {
      if (!map[n.topic]) map[n.topic] = { ...n, count:0, latestDate:n.note_date };
      map[n.topic].count++;
    });
    return Object.values(map);
  }, [notes]);

  return (
    <>
      <div style={{ padding:"12px 0 10px" }}>
        <div style={{ fontSize:20, fontWeight:800, color:"#fff" }}>
          {HABITAT_ICONS[habitat]||REALM_ICONS[realm]||"📌"} {habitat||realm}
        </div>
        <div style={{ fontSize:13, color:"#94a3b8", marginTop:3 }}>
          {groups.length} topic{groups.length!==1?"s":""} — tap to read all dates
        </div>
      </div>
      {groups.length===0
        ? <div style={{ textAlign:"center", color:"#64748b", padding:"40px 0" }}>No notes here yet.</div>
        : <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(155px,1fr))", gap:14 }}>
            {groups.map(g => <TopicCard key={g.topic} group={g} lang={lang} onClick={() => onOpenTopic(g.topic)} />)}
          </div>
      }
    </>
  );
}

// ── Language choice screen — shown ONCE, before anything else ─────────────────
// The student's answer here is the fixed language for the whole reading
// session (persisted in localStorage so it also sticks across visits) —
// nothing later should silently switch it back and forth per note.
const READING_LANG_KEY = "vk_reading_lang";

function LanguageChoiceScreen({ onChoose }) {
  return (
    <div style={{
      minHeight:"100vh", background:"linear-gradient(160deg,#0f172a 0%,#162416 60%,#0f172a 100%)",
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      padding:20, fontFamily:"'Segoe UI','Noto Sans Tamil',sans-serif", textAlign:"center",
    }}>
      <div style={{ fontSize:56, marginBottom:10 }}>📖</div>
      <div style={{ fontSize:23, fontWeight:800, color:"#fff", marginBottom:8, fontFamily:"'Noto Sans Tamil',sans-serif" }}>
        மொழியைத் தேர்ந்தெடுக்கவும்
      </div>
      <div style={{ fontSize:20, fontWeight:800, color:"#fff", marginBottom:14 }}>
        Choose your language
      </div>
      <div style={{ fontSize:13, color:"#94a3b8", marginBottom:32, maxWidth:340 }}>
        இந்த மொழியில் வாசிப்பு மற்றும் ஒலி தொடரும் — Reading and audio will stay in this language
      </div>
      <div style={{ display:"flex", gap:16, flexWrap:"wrap", justifyContent:"center" }}>
        <button onClick={() => onChoose("ta")} style={{
          background:"linear-gradient(135deg,#f59e0b 0%,#d97706 100%)", color:"#fff", border:"none",
          borderRadius:18, padding:"22px 34px", fontSize:20, fontWeight:800, cursor:"pointer",
          fontFamily:"'Noto Sans Tamil',sans-serif", minWidth:170, boxShadow:"0 8px 24px rgba(217,119,6,0.35)",
        }}>
          🇮🇳 தமிழ்
        </button>
        <button onClick={() => onChoose("en")} style={{
          background:"linear-gradient(135deg,#16a34a 0%,#15803d 100%)", color:"#fff", border:"none",
          borderRadius:18, padding:"22px 34px", fontSize:20, fontWeight:800, cursor:"pointer",
          minWidth:170, boxShadow:"0 8px 24px rgba(21,128,61,0.35)",
        }}>
          🇬🇧 English
        </button>
      </div>
      <div style={{ fontSize:11, color:"#64748b", marginTop:26, maxWidth:300 }}>
        You can still switch inside a note if you want — this just picks the starting language.
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ReadingNotesPage() {
  // Asked ONCE, up front — persisted so it also sticks across visits.
  const [langChoice, setLangChoice] = useState(() => {
    try { return localStorage.getItem(READING_LANG_KEY) || ""; } catch { return ""; }
  });
  const [stack,   setStack]   = useState([{ level:"home" }]);
  const [realms,  setRealms]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null); // { notes, index, autoPlay }
  const [latestNotes, setLatest] = useState([]);
  const { speaking, speakList, stop } = useTTS();

  const current = stack[stack.length-1];

  const chooseLang = useCallback((l) => {
    try { localStorage.setItem(READING_LANG_KEY, l); } catch { /* private browsing etc. — fine, just won't persist */ }
    setLangChoice(l);
  }, []);

  useEffect(() => {
    Promise.all([
      fetch(`${BASE_API_URL}/readingnotes/realms`).then(r=>r.json()),
      fetch(`${BASE_API_URL}/readingnotes/latest?days=2`).then(r=>r.json()),
    ]).then(([rms, lat]) => {
      setRealms(Array.isArray(rms) ? rms : []);
      setLatest(Array.isArray(lat) ? lat : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const goHome = () => setStack([{ level:"home" }]);

  const selectRealm = useCallback(async (realm) => {
    const r = await fetch(`${BASE_API_URL}/readingnotes/habitats?realm=${encodeURIComponent(realm)}`);
    const habitats = await r.json();
    if (Array.isArray(habitats) && habitats.length===1) {
      selectHabitat(realm, habitats[0].habitat);
    } else {
      setStack(s => [...s, { level:"habitat", realm, habitats: Array.isArray(habitats) ? habitats : [] }]);
    }
  }, []); // eslint-disable-line

  const selectHabitat = useCallback(async (realm, habitat) => {
    const r = await fetch(`${BASE_API_URL}/readingnotes/archive?realm=${encodeURIComponent(realm)}&habitat=${encodeURIComponent(habitat)}&limit=200`);
    const notes = await r.json();
    setStack(s => {
      const base = s.filter(x => x.level!=="topics");
      return [...base, { level:"topics", realm, habitat, notes: Array.isArray(notes) ? notes : [] }];
    });
  }, []);

  const openTopicSlides = useCallback(async (topicName) => {
    const r = await fetch(`${BASE_API_URL}/readingnotes/bytopic?topic=${encodeURIComponent(topicName)}`);
    const notes = await r.json();
    if (Array.isArray(notes) && notes.length>0) {
      setModal({ notes, index:0, autoPlay:false });
    }
  }, []);

  // Read Latest: open modal with latest notes AND auto-read
  const handleReadLatest = useCallback(() => {
    if (speaking) { stop(); return; }
    if (latestNotes.length===0) return;
    setModal({ notes: latestNotes, index:0, autoPlay:true });
  }, [speaking, stop, latestNotes]);

  const breadcrumb = stack.filter(s => s.realm || s.habitat);

  if (!langChoice) return <LanguageChoiceScreen onChoose={chooseLang} />;

  if (loading) return (
    <div style={{ minHeight:"100vh", background:"#0f172a", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ textAlign:"center", color:"#94a3b8" }}>
        <div style={{ fontSize:52 }}>📖</div>
        <div style={{ marginTop:12 }}>ஏற்றுகிறது... Loading...</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:"linear-gradient(160deg,#0f172a 0%,#162416 60%,#0f172a 100%)", fontFamily:"'Segoe UI','Noto Sans Tamil',sans-serif", color:"#e2e8f0", padding:"12px 14px 80px" }}>

      {/* Top nav */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12, flexWrap:"wrap", gap:8 }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
          <button onClick={goHome} style={{ background:"none", border:"none", color:stack.length===1?"#fff":"#94a3b8", fontWeight:700, fontSize:15, cursor:"pointer", padding:0 }}>
            🏠 Reading
          </button>
          {breadcrumb.map((s, i) => (
            <React.Fragment key={i}>
              <span style={{ color:"#475569" }}>›</span>
              <button onClick={() => setStack(st => st.slice(0, stack.indexOf(s)+1))}
                style={{ background:"none", border:"none", color:i===breadcrumb.length-1?"#fff":"#94a3b8", fontWeight:700, fontSize:14, cursor:"pointer", padding:0 }}>
                {s.habitat||s.realm}
              </button>
            </React.Fragment>
          ))}
        </div>
        {latestNotes.length>0 && (
          <button onClick={handleReadLatest}
            style={{ background:speaking?"#dc2626":"#16a34a", color:"#fff", border:"none", borderRadius:10, padding:"7px 14px", fontWeight:700, fontSize:12, cursor:"pointer" }}>
            {speaking ? "⏹ நிறுத்து" : `🔊 புதிய வாசிப்பு (${latestNotes.length})`}
          </button>
        )}
      </div>

      {stack.length>1 && (
        <button onClick={() => setStack(s => s.slice(0,-1))}
          style={{ background:"rgba(255,255,255,0.08)", border:"none", color:"#94a3b8", borderRadius:9, padding:"6px 14px", fontSize:13, cursor:"pointer", marginBottom:14 }}>
          ← பின்னால் / Back
        </button>
      )}

      {current.level==="home"    && <HomeLevel realms={realms} onSelect={selectRealm} />}
      {current.level==="habitat" && <HabitatLevel realm={current.realm} habitats={current.habitats} onSelect={h => selectHabitat(current.realm, h)} />}
      {current.level==="topics"  && <TopicsLevel realm={current.realm} habitat={current.habitat} notes={current.notes} lang={langChoice} onOpenTopic={openTopicSlides} />}

      {modal && (
        <SlideModal
          notes={modal.notes}
          startIndex={modal.index}
          autoPlay={modal.autoPlay}
          lang={langChoice}
          onClose={() => { stop(); setModal(null); }}
        />
      )}
    </div>
  );
}
